import React, { useState, useEffect } from "react";
import { apiFetch } from "../../../core/utils/api";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";
import useMatchMedia from "../../../core/hooks/useMatchMedia";

// Custom icon components
const ChevronLeftIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
  </svg>
);

const ChevronRightIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
  </svg>
);

const getCompactTemplateDisplayName = (template, isMobileCompact) => {
  if (!isMobileCompact) return template?.name || "";

  const templateValue = String(template?.value || "").trim();
  if (templateValue === "navbar-navigation") return "Portfolio";
  if (templateValue === "sidebar-navigation") return "Book";
  if (templateValue === "single-page") return "Single Page";
  return template?.name || "";
};

const TemplateSelection = ({
  selectedTemplate,
  onTemplateSelect,
  itemsPerPage = 4,
  variant = "grid",
  useColorAccents = true,
  templates: templatesOverride = null,
}) => {
  const [internalTemplates, setInternalTemplates] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [internalError, setInternalError] = useState(null);

  const ITEMS_PER_PAGE = Math.max(1, itemsPerPage); // Number of items to show per row
  const isCompact = variant === "compact";
  const isMobileCompact = useMatchMedia("(max-width: 639px)");
  const isControlled = Array.isArray(templatesOverride);
  const templates = isControlled ? templatesOverride : internalTemplates;
  const loading = isControlled ? false : internalLoading;
  const error = isControlled ? null : internalError;

  useEffect(() => {
    if (isControlled) return;
    fetchTemplates();
  }, [isControlled]);

  const fetchTemplates = async () => {
    try {
      setInternalLoading(true);
      const response = await apiFetch("/api/website-templates/");
      if (!response.ok) {
        throw new Error("Failed to fetch templates");
      }
      const data = await response.json();
      setInternalTemplates(data);
    } catch (err) {
      console.error("Error fetching templates:", err);
      setInternalError(err.message);
    } finally {
      setInternalLoading(false);
    }
  };

  const totalPages = Math.ceil(templates.length / ITEMS_PER_PAGE);
  const currentTemplates = templates.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getTemplateColors = (templateValue) => {
    const colorMap = {
      "navbar-navigation": { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-700", initial: "bg-emerald-600" },
      "sidebar-navigation": { bg: "bg-indigo-100", border: "border-indigo-300", text: "text-indigo-700", initial: "bg-indigo-600" },
      "single-page": { bg: "bg-slate-100", border: "border-slate-300", text: "text-slate-700", initial: "bg-slate-600" },
    };
    return colorMap[templateValue] || colorMap["single-page"];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        <span className={`ml-2 ${typography.body} ${textColors.primary}`}>Loading templates...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className={`${typography.body} text-red-600 dark:text-red-400 mb-2`}>Failed to load templates</div>
        <button
          onClick={fetchTemplates}
          className={`px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 ${typography.button}`}
        >
          Try Again
        </button>
      </div>
    );
  }

  const renderTemplateCard = (template) => {
    const colors = getTemplateColors(template.value);
    const isSelected = selectedTemplate?.value === template.value;
    const baseClasses = isCompact
      ? `cursor-pointer rounded-md border px-3 py-2 transition-all ${typography.bodySmall} flex items-center justify-center text-center`
      : "group relative cursor-pointer rounded-lg border-2 p-3 transition-all duration-200 hover:shadow";
    const stateClasses = isSelected
      ? useColorAccents
        ? `${colors.border} ${colors.bg} ring-2 ring-offset-2 ring-emerald-500`
        : `${borders.default} ${surfaces.secondary} ring-2 ring-offset-2 ring-emerald-500`
      : useColorAccents
        ? `border-gray-200 dark:border-gray-700 ${surfaces.primary} hover:${colors.bg} hover:${colors.border}`
        : `${borders.default} ${surfaces.primary} ${surfaces.hover}`;

    return (
      <div
        key={template.value}
        onClick={() => onTemplateSelect(template)}
        className={`${baseClasses} ${stateClasses}`}
      >
        {isCompact ? (
          <div className="flex w-full items-center justify-center gap-2 text-center">
            {useColorAccents ? <span className={`h-2 w-2 shrink-0 rounded-full ${colors.initial}`} /> : null}
            <span
              className={`block leading-tight ${textColors.primary} ${
                isSelected ? "font-semibold" : "font-medium"
              }`}
            >
              {getCompactTemplateDisplayName(template, isMobileCompact)}
            </span>
          </div>
        ) : (
          <>
            {/* Selection Indicator */}
            {isSelected && (
              <div className="absolute top-2 right-2">
                <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}

            {/* Template Initial Square */}
            <div className="flex items-center mb-2">
              <div className={`
                w-12 h-12 ${colors.initial} rounded-lg flex items-center justify-center mr-3
              `}>
                <span className="text-white font-bold text-lg">
                  {template.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className={`${typography.body} font-semibold ${isSelected ? colors.text : textColors.primary}`}>
                  {template.name}
                </h3>
              </div>
            </div>

            {/* Description: show for all cards when parent grid is hovered (desktop); collapse height when hidden */}
            <p className={`${typography.bodySmall} ${isSelected ? colors.text : textColors.secondary} hidden xl:block xl:max-h-0 xl:overflow-hidden xl:group-hover:max-h-20 xl:group-hover:opacity-100 xl:transition-all xl:duration-150`}>
              {template.description}
            </p>
          </>
        )}
      </div>
    );
  };

  // In compact mode:
  // - When there are multiple pages, keep chip widths consistent across pages (last page should NOT stretch).
  // - When there's only one page, it's nicer to let chips expand to fill the row.
  const compactColumns =
    totalPages > 1 ? ITEMS_PER_PAGE : Math.max(1, currentTemplates.length || 0);
  const gridClassName = isCompact ? "grid gap-2 w-full" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 group";

  const selectionGrid = (
    <div
      className={gridClassName}
      style={isCompact ? { gridTemplateColumns: `repeat(${compactColumns}, minmax(0, 1fr))` } : undefined}
    >
      {currentTemplates.map((template) => renderTemplateCard(template))}
    </div>
  );

  return (
    <div className={isCompact ? "space-y-2" : "space-y-3"}>
      {/* Template Options Grid */}
      {isCompact ? (
        totalPages > 1 ? (
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevPage}
              disabled={currentPage === 0}
              className={`p-2 rounded-full border ${borders.default} transition-colors ${
                currentPage === 0
                  ? `${textColors.muted} cursor-not-allowed`
                  : `${textColors.secondary} ${textColors.hover} ${surfaces.hover}`
              }`}
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <div className="flex-1 flex justify-center">{selectionGrid}</div>
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages - 1}
              className={`p-2 rounded-full border ${borders.default} transition-colors ${
                currentPage === totalPages - 1
                  ? `${textColors.muted} cursor-not-allowed`
                  : `${textColors.secondary} ${textColors.hover} ${surfaces.hover}`
              }`}
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          selectionGrid
        )
      ) : (
        selectionGrid
      )}

      {/* Pagination */}
      {!isCompact && totalPages > 1 && (
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={goToPrevPage}
            disabled={currentPage === 0}
            className={`
              p-2 rounded-full transition-colors
              ${currentPage === 0 
                ? `${textColors.muted} cursor-not-allowed` 
                : `${textColors.secondary} ${textColors.hover} ${surfaces.hover}`
              }
            `}
          >
            <ChevronLeftIcon />
          </button>

          <div className="flex space-x-1">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i)}
                className={`
                  w-8 h-8 rounded-full ${typography.bodySmall} font-medium transition-colors
                  ${i === currentPage 
                    ? 'bg-emerald-600 text-white' 
                    : `${textColors.secondary} ${surfaces.hover}`
                  }
                `}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages - 1}
            className={`
              p-2 rounded-full transition-colors
              ${currentPage === totalPages - 1 
                ? `${textColors.muted} cursor-not-allowed` 
                : `${textColors.secondary} ${textColors.hover} ${surfaces.hover}`
              }
            `}
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}

      {/* Page Info */}
      {!isCompact && totalPages > 1 && (
        <div className={`text-center ${typography.bodySmall} ${textColors.secondary}`}>
          Showing {currentPage * ITEMS_PER_PAGE + 1} - {Math.min((currentPage + 1) * ITEMS_PER_PAGE, templates.length)} of {templates.length} templates
        </div>
      )}
    </div>
  );
};

export default TemplateSelection; 